"use client";

import { ChartWrapper } from "charts/src/components/chart";
import Image from "next/image";
import React from "react";
import { z } from "zod";

import { ChartConfigDetails } from "@/features/analysis/services/get-chart-config-details";
import { timeFrameSchema } from "@/features/analysis/services/get-chart-config-list";
import { Lang } from "@/i18n-config";
import { formatSymbol } from "@/utils/i18n/format-symbol";

import { apiDataToInstanceData } from "../data-convert/api-data-to-instance-data";
import { apiDataToInstanceDataConfig } from "../data-convert/api-data-to-instance-data-config";
import { apiDataToInstanceLabels } from "../data-convert/api-data-to-instance-labels";
import { apiDataToWatermarkPosition } from "../data-convert/api-data-to-watermark-position";
import {
  DUMMY_RANGE,
  DUMMY_VALUE_FORMAT_TYPE,
  TITLE_ICON_SIZE,
  WATERMARK_SIZE_DIALOG,
  getChartUiConfig,
} from "../data/chart-configs";

type ChartGridItemProps = {
  symbol: string;
  lang: Lang;
  timeFrame: z.infer<typeof timeFrameSchema>[number];
  chartId: string;
  chartConfigDetails: ChartConfigDetails;
};

export const ChartDialog = ({
  chartId,
  chartConfigDetails,
  timeFrame,
  lang,
  symbol,
}: ChartGridItemProps) => {
  const converterInputParams = {
    chartConfigDetails,
    timeFrame,
    chartId,
    symbol,
    lang,
    mode: "dialog" as const,
  };

  const instanceData = apiDataToInstanceData(converterInputParams);
  const instanceDataConfig = apiDataToInstanceDataConfig(converterInputParams);
  const instanceLabels = apiDataToInstanceLabels(converterInputParams);
  const watermarkPosition = apiDataToWatermarkPosition(
    converterInputParams,
    true
  );

  // 非多數據圖表，仍需第一個圖表的 type
  const firstChartType = chartConfigDetails.chartDatas[0].chartType;
  const { colorSet, iconSrc } = getChartUiConfig(
    chartId,
    instanceDataConfig.length
  );

  return (
    <div>
      <Dialog
        title={
          <>
            <Image
              src={iconSrc}
              alt={`${symbol} ${chartConfigDetails.title}`}
              className="relative top-[1px]"
              width={TITLE_ICON_SIZE}
              height={TITLE_ICON_SIZE}
            />
            {`${formatSymbol(symbol)} ${chartConfigDetails.title}`}
          </>
        }
        body={
          <>
            <Image
              src="/watermark/growin.png"
              className="absolute z-10 opacity-20"
              style={{
                left: watermarkPosition.left,
                top: watermarkPosition.top,
              }}
              alt="Growin"
              width={WATERMARK_SIZE_DIALOG}
              height={WATERMARK_SIZE_DIALOG}
            />
            <ChartWrapper
              isControllable={false}
              valueFormatType={DUMMY_VALUE_FORMAT_TYPE}
              className="!bg-[#FFF]"
              chartKey="financial_metrices_basic_chart"
              chartType={firstChartType}
              range={DUMMY_RANGE}
              timeFrame={timeFrame}
              symbols={[symbol]}
              locale={lang}
              instanceData={instanceData}
              instanceLabels={instanceLabels}
              instanceDataConfig={instanceDataConfig}
              customColorset={colorSet}
            />
          </>
        }
      />
    </div>
  );
};

const ChartDialogWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="w-full p-4 pt-0">{children}</div>;
};

const ChartDialogBody = ({ children }: { children: React.ReactNode }) => {
  return <div className="relative sm:h-[60vh]">{children}</div>;
};

const ChartDialogTitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <h3 className="relative z-10 mb-[-24px] flex items-center justify-center gap-2 text-2xl font-semibold text-gray-700">
      {children}
    </h3>
  );
};

const Dialog = ({
  title = "",
  body,
}: {
  title?: React.ReactNode;
  body: React.ReactNode;
}) => {
  return (
    <ChartDialogWrapper>
      <ChartDialogTitle>{title}</ChartDialogTitle>
      <ChartDialogBody>{body}</ChartDialogBody>
    </ChartDialogWrapper>
  );
};
